/**
 * @fileoverview Component for displaying Paddle webhook URL
 * @module paddle-connect/components/WebhookUrlDisplay
 */

import { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Alert,
  AlertTitle,
  Tooltip,
  Snackbar,
} from '@mui/material';
import { PADDLE_CONNECT_TEST_IDS } from '@modules/paddle-connect/testIds';
import { ContentCopy, CheckCircle, Link as LinkIcon } from '@mui/icons-material';

/**
 * Get the webhook URL based on environment
 *
 * IMPORTANT: Webhook URLs must be publicly accessible - Paddle servers
 * cannot reach localhost. We use VITE_EDGE_GATEWAY_URL which always
 * points to the cloud server, even in local development.
 */
export function getWebhookUrl(projectId: string): string {
  // Destination identity is required even when the event has no custom_data.
  const query = new URLSearchParams({ projectId }).toString();
  // Use VITE_EDGE_GATEWAY_URL for webhooks - must be publicly accessible
  // (VITE_BUILDER_API_URL may point to localhost in local dev)
  const edgeGatewayUrl = import.meta.env.VITE_EDGE_GATEWAY_URL;
  if (edgeGatewayUrl) {
    return `${edgeGatewayUrl.replace(/\/+$/, '')}/api/paddle/webhooks?${query}`;
  }

  // Fallback to builder URL only if it's not localhost
  const builderUrl = import.meta.env.VITE_BUILDER_API_URL;
  if (builderUrl && !builderUrl.includes('localhost')) {
    return `${builderUrl.replace(/\/+$/, '')}/api/paddle/webhooks?${query}`;
  }

  // Production fallback: derive from current origin
  const origin = window.location.origin;
  const apiOrigin = origin.replace('://app.', '://api.');
  return `${apiOrigin}/api/paddle/webhooks?${query}`;
}

export interface WebhookUrlDisplayProps {
  /** Project whose webhook destination is being configured. */
  projectId: string;
  /** Whether to show expanded setup instructions */
  showInstructions?: boolean;
  /** Compact mode for smaller display */
  compact?: boolean;
}

/**
 * WebhookUrlDisplay Component
 *
 * Displays the Paddle webhook URL that users need to configure
 * in their Paddle Dashboard with a copy button and setup instructions.
 */
export function WebhookUrlDisplay({
  projectId,
  showInstructions = true,
  compact = false,
}: WebhookUrlDisplayProps) {
  const [copied, setCopied] = useState(false);
  const webhookUrl = getWebhookUrl(projectId);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [webhookUrl]);

  const handleSnackbarClose = useCallback(() => {
    setCopied(false);
  }, []);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: compact ? 1.5 : 2,
        mt: compact ? 1 : 2,
        backgroundColor: 'background.default',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <LinkIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Webhook URL for Paddle Dashboard
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          value={webhookUrl}
          size="small"
          fullWidth
          slotProps={{
            htmlInput: { 'data-testid': PADDLE_CONNECT_TEST_IDS.webhookUrl },
            input: {
              readOnly: true,
              sx: {
                fontFamily: 'monospace',
                fontSize: compact ? '0.75rem' : '0.875rem',
              },
            },
          }}
        />
        <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
          <IconButton
            onClick={handleCopy}
            size="small"
            color={copied ? 'success' : 'default'}
          >
            {copied ? <CheckCircle /> : <ContentCopy />}
          </IconButton>
        </Tooltip>
      </Box>

      {showInstructions && (
        // Left-aligned even when a host centres this card (Paddle not-connected card, D-18 / C-20).
        <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }} icon={false}>
          <AlertTitle sx={{ fontSize: '0.875rem' }}>Setup Instructions</AlertTitle>
          <Box
            component="ol"
            sx={{
              m: 0,
              pl: 2.5,
              '& li': {
                fontSize: '0.8125rem',
                mb: 0.5,
                color: 'text.secondary',
              },
            }}
          >
            <li>Go to <strong>Paddle Dashboard → Developer Tools → Notifications</strong></li>
            <li>Click <strong>"New Destination"</strong> and paste the webhook URL above, including your project ID</li>
            <li>
              Subscribe to events:{' '}
              <code style={{ fontSize: '0.75rem', backgroundColor: 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: '3px' }}>
                transaction.*
              </code>,{' '}
              <code style={{ fontSize: '0.75rem', backgroundColor: 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: '3px' }}>
                subscription.*
              </code>,{' '}
              <code style={{ fontSize: '0.75rem', backgroundColor: 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: '3px' }}>
                adjustment.*
              </code>,{' '}
              <code style={{ fontSize: '0.75rem', backgroundColor: 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: '3px' }}>
                customer.*
              </code>
            </li>
            <li>Copy the destination’s <strong>Secret key</strong> and paste it in "Advanced Settings" above to enable payment event verification</li>
          </Box>
        </Alert>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message="Webhook URL copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
}

export default WebhookUrlDisplay;
